import axios, { AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios';
import {
  API_RATE_LIMIT,
  HTTP_STATS_CODE,
  LOCAL_STORAGE
} from 'constants/general';
import {
  getLocalStorage,
  removeLocalStorage,
  updateLocalStorage
} from 'utils/helpers/storage';

export class ApiRateLimitedError extends Error {
  readonly name = 'ApiRateLimitedError';

  constructor() {
    super('API rate limited');
  }
}

export const createApiRateLimitedError = () => new ApiRateLimitedError();

export const isApiRateLimitedError = (
  error: unknown
): error is ApiRateLimitedError => error instanceof ApiRateLimitedError;

type ResponseHeaders = AxiosResponseHeaders | RawAxiosResponseHeaders;

const getRetryAfterHeaderValue = (
  headers: ResponseHeaders
): string | undefined => {
  const value = headers[API_RATE_LIMIT.RESPONSE_HEADER];
  if (value === undefined || value === null) {
    return undefined;
  }
  return Array.isArray(value) ? value[0] : String(value);
};

export const parseRetryAfterMs = (
  headers: ResponseHeaders | undefined
): number | null => {
  if (!headers) {
    return null;
  }

  const raw = getRetryAfterHeaderValue(headers);
  if (raw === undefined) {
    return null;
  }

  const seconds = Number.parseInt(raw, 10);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }

  return seconds * API_RATE_LIMIT.COUNTDOWN_TICK_INTERVAL_MS;
};

export const getApiRateLimitBlockedUntil = (): number | null => {
  const stored = getLocalStorage(LOCAL_STORAGE.API_RATE_LIMITED_UNTIL);
  if (stored === null || stored === undefined) {
    return null;
  }

  const until = Number.parseInt(String(stored), 10);
  if (!Number.isFinite(until) || until <= 0) {
    return null;
  }

  return until;
};

export const setApiRateLimitBlockedUntil = (until: number): number => {
  updateLocalStorage(LOCAL_STORAGE.API_RATE_LIMITED_UNTIL, String(until));
  return until;
};

export const clearApiRateLimitBlockedUntil = (): void => {
  removeLocalStorage([LOCAL_STORAGE.API_RATE_LIMITED_UNTIL]);
};

export const isApiRateLimited = (until: number | null): boolean =>
  until !== null && Date.now() < until;

export const getApiRateLimitRemainingMs = (until: number | null): number => {
  if (!until) {
    return 0;
  }
  return Math.max(0, until - Date.now());
};

export const formatApiRateLimitRemainingSeconds = (
  remainingMs: number
): number => Math.ceil(remainingMs / API_RATE_LIMIT.COUNTDOWN_TICK_INTERVAL_MS);

export const isRateLimitAxiosError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === HTTP_STATS_CODE.TOO_MANY_REQUEST;

export const isRateLimitError = (error: unknown): boolean =>
  isRateLimitAxiosError(error) || isApiRateLimitedError(error);
